import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { DocumentoAccredito, Evento } from '../../../../shared/interface';
import { Table } from 'primeng/table';
import { ApiService } from '../../../../services/api.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopup } from 'primeng/confirmpopup';

@Component({
  selector: 'app-documenti-accrediti-backoffice',
  templateUrl: './documenti-accrediti-backoffice.component.html',
  styleUrls: ['./documenti-accrediti-backoffice.component.css']
})
export class DocumentiAccreditiBackofficeComponent {

  constructor(
    private apiService: ApiService,
    private confirmationService: ConfirmationService
  ) {}

  @ViewChild('delete') delete!: ConfirmPopup;

  @Input() documenti: DocumentoAccredito[] = [];  
  @Input() evento?: Evento;

  documento?: DocumentoAccredito;

  loading: boolean = false;    

  ngOnInit(): void {
    
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.documenti = changes['documenti']?.currentValue || this.documenti;    
    this.evento = changes['evento']?.currentValue || this.evento;    
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  onOpenDocumento(file: string) {
    this.apiService.openProtectedFile(file);
  }

  /** JOIN API + fallback dalla lista subappalti dell'evento (se il nome non arriva). */
  labelSubappalto(documento: DocumentoAccredito): string {
    if (documento.subappalto) return documento.subappalto;
    const id = documento.id_subappalto;
    if (!id) return '';
    const s = (this.evento?.subappalti || []).find(x => Number(x.id) === Number(id));
    return s?.ragione_sociale || '';
  }

  onOpenDelete(event: Event, documento: DocumentoAccredito, index: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Sei sicuro di voler rimuovere il documento?`,
      reject: () => this.confirmationService.close(),
      accept: () => {        
        this.apiService.deleteDocumentoAccredito(documento.id).subscribe({
          next: (data) => this.apiService.setDocumentiEvento(this.evento!.id),
          error: (err) => this.apiService.message('error', 'Errore', err.error.message.message),
          complete: () => {
            this.confirmationService.close()
            this.apiService.message("success", "Eliminazione", "Il documento è stato eliminato con successo!")
          }
        })
      },
    });
  }

  onDelete() {
    this.delete.accept();
  }

  onClose() {
    this.delete.reject();
  }

  onZip() {
    this.loading = true;
    // Recupero la ricevuta
    this.apiService.getZipDocumenti(this.documenti).subscribe({
      next: (data) => {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.target = '_blank'
        a.href = url;
        a.download = `documenti.zip`;
        a.click();
        this.loading = false;
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);       
      },
      error: (err) => {
        this.loading = false;
        this.apiService.message('error', 'Errore', err.message)        
      },
    });
  }

  onStato(stato: string, documento: DocumentoAccredito) {
    // Rilancio sullo stesso pallino → toglie lo stato (torna “da accettare” / ok)
    const next = documento.stato === stato ? 'ok' : stato;
    this.loading = true;
    this.apiService.updateStatoDocumento(next, documento.id).subscribe({
      next: () => {
        documento.stato = next;
        if (this.evento?.id) this.apiService.documentoStatoChanged.next(this.evento.id);
      },
      error: (err) => {
        this.loading = false;
        this.apiService.message('error', 'Errore', err.message)        
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  scadenzaDate(documento: DocumentoAccredito): Date | null {
    if (!documento.data_scadenza) return null;
    const d = new Date(documento.data_scadenza);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  isScaduto(documento: DocumentoAccredito): boolean {
    if (!documento.data_scadenza) return false;
    const d = new Date(documento.data_scadenza);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d.getTime() < today.getTime();
  }

  private toYmd(value: Date | null): string | null {
    if (!value || Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  onScadenza(value: Date | null, documento: DocumentoAccredito) {
    const iso = this.toYmd(value);
    this.loading = true;
    this.apiService.updateScadenzaDocumento(iso, documento.id).subscribe({
      next: () => {
        documento.data_scadenza = iso;
        this.apiService.message('success', 'Scadenza', iso ? 'Data aggiornata' : 'Scadenza rimossa');
      },
      error: (err) => {
        this.loading = false;
        this.apiService.message('error', 'Errore', err?.error?.message?.message || err.message);
      },
      complete: () => { this.loading = false; },
    });
  }

  private readonly impiantiTipi = new Set([
    'impianti_elettrici', 'impianti_luci', 'impianti_audio', 'impianti_video',
    'effetti_scenici', 'macchina_fumo', 'carichi_sospesi', 'certificazione_impianti_extra', 'montaggio',
  ]);

  isImpianto(documento: DocumentoAccredito): boolean {
    return this.impiantiTipi.has(documento.tipo);
  }

  private notaDraft = new Map<number, string>();

  onNotaDraft(documento: DocumentoAccredito, value: string) {
    this.notaDraft.set(documento.id, value);
  }

  onNotaBlur(documento: DocumentoAccredito) {
    const note = this.notaDraft.has(documento.id) ? this.notaDraft.get(documento.id)! : (documento.note || '');
    if (note === (documento.note || '')) return;
    this.apiService.updateNotaDocumento(note || null, documento.id).subscribe({
      next: () => {
        documento.note = note;
        this.apiService.message('success', 'Note', 'Nota aggiornata');
      },
      error: (err) => this.apiService.message('error', 'Errore', err?.error?.message?.message || err.message),
    });
  }
}
